import { CalendarDate, DateValue } from '@internationalized/date';
import { RangeValue } from '@react-types/shared';
import { isEmpty, keyBy } from 'lodash/fp';
import { DateTime } from 'luxon';
import { useMemo } from 'react';

import Pagination from 'dashboard/components/Pagination';
import { MonoSpan } from 'dashboard/components/Typography';
import LoadingIndicator from 'dashboard/components-lib/LoadingIndicator';
import { useLabelTextMapper } from 'dashboard/components-lib/MultiSelect';
import TableControls, { FilterConfig } from 'dashboard/components-lib/TableControls';
import { Container, Header, Section } from 'dashboard/layouts/IndexViewLayoutV2';
import TableLayout, {
  TableLinkTR,
  TableNullState,
  TableTD,
  TableTH,
} from 'dashboard/layouts/TableLayout';
import { User, UserStatus } from 'dashboard/models/user';
import { UserAuditLog, UserAuditLogAttributes } from 'dashboard/models/user-audit-log';
import { UserAuditLogsFilterParams } from 'dashboard/redux/actions/user-audit-logs';
import Card from 'design-system/components/Card';
import { DATE_AND_TIME_FORMAT, TIMEZONE_NAME_ABBREVIATED } from 'lib/constants/luxonFormats';
import { PaginationParams } from 'lib/interfaces/api';

const METHOD_FILTER_OPTIONS = [
  {
    label: 'GET',
    value: 'GET',
  },
  {
    label: 'POST',
    value: 'POST',
  },
  {
    label: 'PATCH',
    value: 'PATCH',
  },
  {
    label: 'DELETE',
    value: 'DELETE',
  },
];

const API_PREFIX_LENGTH = '/api/internal'.length;

interface DashboardUserAuditLogsViewProps {
  userAuditLogs: UserAuditLog[];
  userAuditLogsLoading: boolean;
  users: User[];
  usersLoading: boolean;
  pageSize: number;
  paginationParams: PaginationParams;
  onPageSizeClick: (pageSize: number) => void;
  onPageLinkClick: (pageNumber: number) => void;
  filters: UserAuditLogsFilterParams;
  onOmniSearch: (omniQuery: string) => void;
  onDateUpdate: (range: RangeValue<DateValue> | null) => void;
  onUserFilterUpdate: (userId: string) => void;
  onMethodsFilterUpdate: (methods: UserAuditLogAttributes['method'][]) => void;
}

const DashboardUserAuditLogsView = ({
  userAuditLogs,
  userAuditLogsLoading,
  users,
  usersLoading,
  pageSize,
  paginationParams,
  onPageSizeClick,
  onPageLinkClick,
  filters,
  onOmniSearch,
  onDateUpdate,
  onUserFilterUpdate,
  onMethodsFilterUpdate,
}: DashboardUserAuditLogsViewProps) => {
  const { omni, createdAtStart, createdAtEnd, methods, userId } = filters;
  const empty = userAuditLogs.length === 0;
  const hasUsedFilters = !Object.values(filters).every(isEmpty);

  const methodFilterLabelTextMapper = useLabelTextMapper(METHOD_FILTER_OPTIONS, 'Methods', {
    emptyIsAll: true,
  });

  const usersToShow = useMemo(
    () => users.filter((user) => user.attributes.status !== UserStatus.Pending),
    [users],
  );

  const userMap = useMemo(() => keyBy('id', usersToShow), [usersToShow]);
  const userFilterOptions = useMemo(
    () =>
      usersToShow.map(({ id, attributes: { 'email-address': emailAddress } }) => ({
        label: emailAddress,
        value: id,
      })),
    [usersToShow],
  );

  const minDate = DateTime.local().minus({ months: 6 });

  const filterConfig: FilterConfig[] = [
    {
      id: 'date-range',
      name: `Dates (${DateTime.local().toFormat(TIMEZONE_NAME_ABBREVIATED)})`,
      type: 'daterange',
      value: {
        startDate: createdAtStart,
        endDate: createdAtEnd,
      },
      datePickerProps: {
        granularity: 'minute',
        minDate: new CalendarDate(minDate.year, minDate.month, minDate.day),
      },
      onChange: onDateUpdate,
    },
    {
      id: 'requestMethods',
      name: 'Request methods',
      type: 'multiselect-legacy',
      selectOptionValues: METHOD_FILTER_OPTIONS,
      labelTextMapper: methodFilterLabelTextMapper,
      value: methods || [],
      onChange: onMethodsFilterUpdate,
    },
    {
      id: 'users',
      name: 'Users',
      type: 'select',
      options: userFilterOptions,
      value: userId,
      onChange: onUserFilterUpdate,
    },
  ];

  return (
    <Container>
      <Header title='Audit Logs'></Header>
      <Section>
        <Card data-test='dashboard-user-audit-logs__table'>
          <Card.Body>
            <TableControls
              searchProps={{
                id: 'user-audit-logs-view__search-input',
                handleInput: onOmniSearch,
                /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                value: omni,
                placeholder: 'Search by request path (i.e. /dashboard/v1/templates) or IP Address',
              }}
              filters={filterConfig}
            />
            {usersLoading && users.length === 0 ? (
              <LoadingIndicator />
            ) : (
              <TableLayout
                dataTest='user-audit-logs-view__table'
                isLoading={userAuditLogsLoading}
                isEmpty={empty}
                showHeader={hasUsedFilters}
                headerTR={
                  <>
                    <TableTH>Request</TableTH>
                    <TableTH>Status Code</TableTH>
                    <TableTH>User</TableTH>
                    <TableTH>IP Address</TableTH>
                    <TableTH>{`Created at (${DateTime.local().toFormat(
                      TIMEZONE_NAME_ABBREVIATED,
                    )})`}</TableTH>
                  </>
                }
                nullState={
                  <TableNullState
                    dataTestBlock='user-audit-logs-view'
                    hasUsedFilters={hasUsedFilters}
                  >
                    No audit logs have been created yet.
                  </TableNullState>
                }
              >
                {userAuditLogs.map((userAuditLog: UserAuditLog) => (
                  <TableLinkTR
                    to={`/dashboard/user-audit-logs/${userAuditLog.id}`}
                    key={userAuditLog.id}
                    data-test={`user-audit-log-table-row__${userAuditLog.id}`}
                    id='user-audit-log-table__row'
                  >
                    <TableTD>
                      <MonoSpan>
                        {userAuditLog.attributes.method}{' '}
                        {userAuditLog.attributes.path?.slice(API_PREFIX_LENGTH)}
                      </MonoSpan>
                    </TableTD>
                    <TableTD>
                      <MonoSpan>{userAuditLog.attributes['response']?.status}</MonoSpan>
                    </TableTD>
                    <TableTD>
                      {userAuditLog.attributes['impersonator-email-address'] ? (
                        <MonoSpan>
                          {'Persona admin '}
                          {userAuditLog.attributes['impersonator-email-address']}
                          {userMap[userAuditLog.relationships.user.data.id]?.attributes?.[
                            'email-address'
                          ]
                            ? ` on behalf of ${
                                userMap[userAuditLog.relationships.user.data.id]?.attributes?.[
                                  'email-address'
                                ]
                              }`
                            : ''}
                        </MonoSpan>
                      ) : (
                        userMap[userAuditLog.relationships.user.data.id]?.attributes?.[
                          'email-address'
                        ]
                      )}
                    </TableTD>
                    <TableTD>
                      <MonoSpan>{userAuditLog.attributes['ip-address']}</MonoSpan>
                    </TableTD>
                    <TableTD>
                      {userAuditLog.attributes['created-at'] != null &&
                        DateTime.fromISO(userAuditLog.attributes['created-at']).toFormat(
                          DATE_AND_TIME_FORMAT,
                        )}
                    </TableTD>
                  </TableLinkTR>
                ))}
              </TableLayout>
            )}
            <Pagination
              pageSize={pageSize}
              paginationParams={paginationParams}
              handlePageSizeClick={onPageSizeClick}
              handlePageLinkClick={onPageLinkClick}
            />
          </Card.Body>
        </Card>
      </Section>
    </Container>
  );
};

export default DashboardUserAuditLogsView;
